import React, { useState, useMemo, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import axios from "axios";
import "./PivotTable.css";

const MAX_GROUPING_LEVELS = 5;
const QUALITY_METRICS = [
  "validity",
  "accuracy",
  "consistency",
  "completeness",
  "freshness",
  "uniqueness",
];

function formatGroupingLabel(field) {
  if (field === "rule_name") return "Rule Name";
  if (field === "rule_description") return "Rule Description";

  return field
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const PivotTable = ({
  tableData,
  paginationData,
  filterList,
  apiLoading,
  onPageChange,
  onPageSizeChange,
  onGroupingChange,
  pivotGrouping = {},
  productId,
  currentFilters,
  page,
  readOnlyPivot,
  searchTerm,
  dashboardId,
  activePeriod,
}) => {
  const location = useLocation();

  // State for data and loading
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(apiLoading);
  const [error, setError] = useState(null);
  const [hierarchyData, setHierarchyData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState(null);
  const tableContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [pivotConfig, setPivotConfig] = useState({
    groupBy: [],
    expandedGroups: new Set(),
    sortBy: null,
    sortDirection: "asc",
    filters: {},
  });
  const BASE_URL = process.env.REACT_APP_API_URL;

  // Update loading state when apiLoading prop changes
  useEffect(() => {
    setLoading(apiLoading);
  }, [apiLoading]);

  // Validate and sanitize tableData
  const validateTableData = (data) => {
    if (!data) return [];
    if (!Array.isArray(data)) {
      console.warn(
        "PivotTable: tableData is not an array, converting to array"
      );
      return Array.isArray(data) ? data : [data];
    }

    // Filter out invalid entries and ensure required fields
    return data
      .filter((item) => {
        if (!item || typeof item !== "object") {
          console.warn("PivotTable: Invalid data item found, skipping:", item);
          return false;
        }
        return true;
      })
      .map((item) => {
        // Ensure all required fields exist with fallbacks
        return {
          rule_name: item.rule_name || "",
          rule_description: item.rule_description || "",
          total_row_count:
            typeof item.total_row_count === "number" ? item.total_row_count : 0,
          error_record_count:
            typeof item.error_record_count === "number"
              ? item.error_record_count
              : 0,
          validity: typeof item.validity === "number" ? item.validity : null,
          accuracy: typeof item.accuracy === "number" ? item.accuracy : null,
          consistency:
            typeof item.consistency === "number" ? item.consistency : null,
          completeness:
            typeof item.completeness === "number" ? item.completeness : null,
          freshness: typeof item.freshness === "number" ? item.freshness : null,
          uniqueness:
            typeof item.uniqueness === "number" ? item.uniqueness : null,
          // Preserve any additional fields (like Brand, Product, etc.)
          ...Object.keys(item).reduce((acc, key) => {
            if (
              ![
                "rule_name",
                "rule_description",
                "total_row_count",
                "error_record_count",
                "validity",
                "accuracy",
                "consistency",
                "completeness",
                "freshness",
                "uniqueness",
              ].includes(key)
            ) {
              acc[key] = item[key];
            }
            return acc;
          }, {}),
        };
      });
  };

  // Validate and sanitize filterList
  const validateFilterList = (filters) => {
    if (!filters) return [];
    if (!Array.isArray(filters)) {
      console.warn(
        "PivotTable: filterList is not an array, converting to array"
      );
      return [filters].filter(Boolean);
    }

    return filters
      .filter((filter) => {
        if (typeof filter !== "string" || !filter.trim()) {
          console.warn("PivotTable: Invalid filter found, skipping:", filter);
          return false;
        }
        return true;
      })
      .map((filter) => filter.trim());
  };

  // Validate and sanitize paginationData
  const validatePaginationData = (pagination) => {
    if (!pagination || typeof pagination !== "object") {
    return {
        currentPage: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0,
      };
    }

    return {
      currentPage: Math.max(1, parseInt(pagination.currentPage) || 1),
      pageSize: Math.max(1, Math.min(100, parseInt(pagination.pageSize) || 10)),
      totalPages: Math.max(1, parseInt(pagination.totalPages) || 1),
      totalRecords: Math.max(0, parseInt(pagination.totalRecords) || 0),
    };
  };

  // Update data when props change
  useEffect(() => {
    try {
      const validatedData = validateTableData(tableData);
      setRawData(validatedData);
      setError(validatedData.length === 0 ? "No Table Data available" : null);
    } catch (err) {
      console.error("PivotTable: Error processing tableData:", err);
      setRawData([]);
      setError("Error processing table data");
    }
  }, [tableData]);

  // Update hierarchy data when filterList changes
  useEffect(() => {
    try {
      const validatedFilters = validateFilterList(filterList);
      const hierarchyObj = {};
      validatedFilters.forEach((field, index) => {
        hierarchyObj[`hierarchy_${index + 1}`] = field;
      });
      setHierarchyData(hierarchyObj);
    } catch (err) {
      console.error("PivotTable: Error processing filterList:", err);
      setHierarchyData({});
    }
  }, [filterList]);

  // Update pagination when paginationData changes
  useEffect(() => {
    try {
      const validatedPagination = validatePaginationData(paginationData);
      setCurrentPage(validatedPagination.currentPage);
      setPageSize(validatedPagination.pageSize);
    } catch (err) {
      console.error("PivotTable: Error processing paginationData:", err);
      setCurrentPage(1);
      setPageSize(10);
    }
  }, [paginationData]);
  // Track if pivotGrouping has been applied to avoid overriding manual selections
  const [appliedPivotGrouping, setAppliedPivotGrouping] = useState(null);

  // Apply grouping based on pivotGrouping prop
  useEffect(() => {
    try {
      if (!pivotGrouping || typeof pivotGrouping !== "object") {
        // No pivot grouping provided, keep current state
        return;
      }

      // Check if this is a new pivotGrouping by comparing with previously applied
      const pivotGroupingString = JSON.stringify(pivotGrouping);
      if (pivotGroupingString === appliedPivotGrouping) {
        // Same pivotGrouping already applied, don't override manual selections
        return;
      }

      // Extract hierarchy fields that have values
      // Handle both "Hierarchy_" and "Hierachy_" (typo in API response)
      const groupingFields = [];
      for (let i = 1; i <= 5; i++) {
        const hierarchyKey1 = `Hierarchy_${i}`;
        const hierarchyKey2 = `Hierachy_${i}`; // Handle typo
        const value =
          pivotGrouping[hierarchyKey1] || pivotGrouping[hierarchyKey2];
        if (value && typeof value === "string" && value.trim() !== "") {
          groupingFields.push(value.trim());
        }
      }

      // Apply grouping if any fields are found
      if (groupingFields.length > 0) {
        setPivotConfig((prev) => ({
          ...prev,
          groupBy: groupingFields,
          expandedGroups: new Set(), // Reset expanded groups when applying new grouping
        }));
        setAppliedPivotGrouping(pivotGroupingString);
      } else {
        // No grouping fields found, reset to no grouping
        setPivotConfig((prev) => ({
          ...prev,
          groupBy: [],
          expandedGroups: new Set(),
        }));
        setAppliedPivotGrouping(pivotGroupingString);
      }
    } catch (err) {
      console.error("PivotTable: Error processing pivotGrouping:", err);
    }
  }, [pivotGrouping, appliedPivotGrouping]);

  // Available columns for grouping (dynamically generated from hierarchy data)
  const availableColumns = useMemo(() => {
    if (rawData.length === 0)
      return { dimensions: [], measures: [], groupingOptions: [] };

    // Get hierarchy fields from API response (non-null and non-empty values)
    const hierarchyFields = Object.values(hierarchyData).filter(
      (value) => value !== null && value !== ""
    );

    // Define measure columns based on API response structure
    const measureColumns = [
      "rule_name",
      "rule_description",
      "validity",
      "accuracy",
      "consistency",
      "completeness",
      "freshness",
      "uniqueness",
      "error_record_count",
    ];

    const groupingOptions = [
      ...hierarchyFields,
      "rule_name",
      "rule_description",
    ];

    return {
      dimensions: hierarchyFields,
      measures: measureColumns,
      groupingOptions: Array.from(new Set(groupingOptions)),
    };
  }, [rawData, hierarchyData]);

  const hasHierarchyFields = availableColumns.dimensions.length > 0;
  const hasGroupingOptions = availableColumns.groupingOptions.length > 0;
  const isPivotMode = pivotConfig.groupBy.length > 0;
  const nonGroupedDimensions = useMemo(() => {
    if (!isPivotMode) return [];
    return availableColumns.dimensions.filter(
      (field) => !pivotConfig.groupBy.includes(field)
    );
  }, [availableColumns.dimensions, pivotConfig.groupBy, isPivotMode]);

  // Pagination logic with error handling
  const totalPages = useMemo(() => {
    try {
      const validatedPagination = validatePaginationData(paginationData);
      return validatedPagination.totalPages;
    } catch (err) {
      console.error("PivotTable: Error calculating totalPages:", err);
      return 1;
    }
  }, [paginationData]);

  const totalRecords = useMemo(() => {
    try {
      const validatedPagination = validatePaginationData(paginationData);
      return validatedPagination.totalRecords;
    } catch (err) {
      console.error("PivotTable: Error calculating totalRecords:", err);
      return rawData.length;
    }
  }, [paginationData, rawData.length]);

  // Pagination handlers with error handling
  const handlePageChange = (page) => {
    try {
      const pageNum = parseInt(page);
      if (isNaN(pageNum) || pageNum < 1 || pageNum > totalPages) {
        console.warn("PivotTable: Invalid page number:", page);
        return;
      }
      // Emit page change to parent component for API call
      if (onPageChange && typeof onPageChange === "function") {
        onPageChange(pageNum);
      }
    } catch (err) {
      console.error("PivotTable: Error changing page:", err);
    }
  };

  // State for pivot mode data
  const [pivotModeData, setPivotModeData] = useState([]);
  const [pivotModeLoading, setPivotModeLoading] = useState(false);
  const [useFallbackData, setUseFallbackData] = useState(false);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const updateScrollButtons = () => {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft + container.clientWidth <
          container.scrollWidth - 1
      );
    };

    updateScrollButtons();

    container.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);

    return () => {
      container.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, [
    rawData.length,
    pivotModeData.length,
    isPivotMode,
    nonGroupedDimensions.length,
    availableColumns.measures.length,
  ]);

  const handleTableScroll = (direction) => {
    const container = tableContainerRef.current;
    if (!container) return;

    const scrollAmount = Math.max(container.clientWidth * 0.8, 200);
    const targetPosition =
      direction === "left"
        ? Math.max(0, container.scrollLeft - scrollAmount)
        : Math.min(
            container.scrollWidth,
            container.scrollLeft + scrollAmount
          );

    container.scrollTo({
      left: targetPosition,
      behavior: "smooth",
    });
  };

  // Fetch all data when entering pivot mode
  useEffect(() => {
    const fetchPivotData = async () => {
      // Only fetch when in pivot mode (groupBy has items)
      if (pivotConfig.groupBy.length > 0) {
        try {
          setPivotModeLoading(true);
          setUseFallbackData(false); // Reset fallback flag
          const data = await fetchExportData({ showLoading: false });
          
          // Check if data is valid and not empty
          if (data && Array.isArray(data) && data.length > 0) {
            setPivotModeData(data);
            setUseFallbackData(false);
          } else {
            // No data or empty data - use fallback
            console.warn("PivotTable: No data received from export API, using fallback data");
            setPivotModeData([]);
            setUseFallbackData(true);
          }
          setPivotModeLoading(false);
        } catch (err) {
          console.error("Error fetching pivot mode data:", err);
          // API call failed - use fallback
          setPivotModeData([]);
          setUseFallbackData(true);
          setPivotModeLoading(false);
        }
      } else {
        // When exiting pivot mode, clear pivot data and reset fallback flag
        setPivotModeData([]);
        setUseFallbackData(false);
      }
    };

    fetchPivotData();
  }, [pivotConfig.groupBy.length]); // Re-fetch when entering/exiting pivot mode

  // Generate pivot data with proper Excel-like row grouping
  const pivotData = useMemo(() => {
    if (!pivotConfig.groupBy.length) {
      // When no grouping is selected, show raw data with individual rows (from props)
      let filteredData = rawData;
      Object.entries(pivotConfig.filters).forEach(([field, value]) => {
        if (value) {
          filteredData = filteredData.filter((item) =>
            String(item[field])
              .toLowerCase()
              .includes(String(value).toLowerCase())
          );
        }
      });

      // Sort by rule_name in ascending order
      filteredData = [...filteredData].sort((a, b) => {
        const nameA = getRuleNameSortValue(a);
        const nameB = getRuleNameSortValue(b);
        return nameA.localeCompare(nameB);
      });

      return filteredData.map((item) => ({
        ...item,
        level: 0,
        isLeaf: true,
        _originalData: [item],
      }));
    }

    // In pivot mode, use pivotModeData (fetched from API) or fallback to rawData
    let filteredData = pivotConfig.groupBy.length > 0 
      ? (useFallbackData ? rawData : pivotModeData) 
      : rawData;
    
    // Apply filters
    Object.entries(pivotConfig.filters).forEach(([field, value]) => {
      if (value) {
        filteredData = filteredData.filter((item) =>
          String(item[field])
            .toLowerCase()
            .includes(String(value).toLowerCase())
        );
      }
    });

    // Aggregate data for measures
    const aggregateData = (data, measureFields) => {
      const aggregated = {};

      measureFields.forEach((field) => {
        if (["rule_name", "rule_description"].includes(field)) {
          return;
        }

        const values = data
          .map((item) => item[field])
          .filter((val) => typeof val === "number" && !isNaN(val));
        if (values.length > 0) {
          // Use sum for count fields, average for percentage fields
          if (field === "total_row_count" || field === "error_record_count") {
            aggregated[field] = values.reduce((sum, val) => sum + val, 0);
          } else {
            aggregated[field] = Math.round(
              values.reduce((sum, val) => sum + val, 0) / values.length
            );
          }
        } else {
          aggregated[field] = 0;
        }
      });

      return aggregated;
    };

    // Simple and reliable grouping logic
    if (pivotConfig.groupBy.length === 1) {
      // Single level grouping
      const [groupField] = pivotConfig.groupBy;
      const grouped = {};

      filteredData.forEach((item) => {
        const key = item[groupField];
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(item);
      });

      const result = [];
      
      // Sort groups by key name in ascending order
      const sortedGroupEntries = Object.entries(grouped).sort((a, b) => {
        const keyA = getGroupSortValue(groupField, a[0]);
        const keyB = getGroupSortValue(groupField, b[0]);
        return keyA.localeCompare(keyB);
      });
      
      sortedGroupEntries.forEach(([key, items]) => {
        const aggregated = aggregateData(items, availableColumns.measures);
        const groupKey = `${groupField}_${key}`;
        
        // Add group header
        result.push({
          [groupField]: key,
          level: 0,
          isGroup: true,
          groupField: groupField,
          groupKey: groupKey,
          _originalData: items,
          _count: items.length,
          ...aggregated,
        });

        // Sort items by rule_name before adding them
        const sortedItems = [...items].sort((a, b) => {
          const nameA = getRuleNameSortValue(a);
          const nameB = getRuleNameSortValue(b);
          return nameA.localeCompare(nameB);
        });

        // Add individual items as leaf nodes
        sortedItems.forEach((item) => {
          result.push({
            ...item,
            level: 1,
            isLeaf: true,
            _originalData: [item],
          });
        });
      });

      return result;
    } else if (pivotConfig.groupBy.length === 2) {
      // Two level grouping
      const [firstField, secondField] = pivotConfig.groupBy;
      const grouped = {};

      // First level grouping
      filteredData.forEach((item) => {
        const key = item[firstField];
        if (!grouped[key]) {
          grouped[key] = {};
        }
        
        const subKey = item[secondField];
        if (!grouped[key][subKey]) {
          grouped[key][subKey] = [];
        }
        grouped[key][subKey].push(item);
      });

      const result = [];
      
      // Sort first level groups by key name in ascending order
      const sortedFirstLevelEntries = Object.entries(grouped).sort((a, b) => {
        const keyA = getGroupSortValue(firstField, a[0]);
        const keyB = getGroupSortValue(firstField, b[0]);
        return keyA.localeCompare(keyB);
      });
      
      sortedFirstLevelEntries.forEach(([firstKey, subGroups]) => {
        // Calculate first level aggregation
        const firstLevelItems = Object.values(subGroups).flat();
        const firstLevelAggregated = aggregateData(
          firstLevelItems,
          availableColumns.measures
        );
        const firstGroupKey = `${firstField}_${firstKey}`;
        
        // Add first level group header
        result.push({
          [firstField]: firstKey,
          level: 0,
          isGroup: true,
          groupField: firstField,
          groupKey: `${firstField}_${firstKey}`,
          _originalData: firstLevelItems,
          _count: Object.keys(subGroups).length, // Count of sub-groups, not total items
          ...firstLevelAggregated,
        });

        // Sort second level groups by key name in ascending order
        const sortedSecondLevelEntries = Object.entries(subGroups).sort((a, b) => {
          const keyA = getGroupSortValue(secondField, a[0]);
          const keyB = getGroupSortValue(secondField, b[0]);
          return keyA.localeCompare(keyB);
        });

        // Process second level
        sortedSecondLevelEntries.forEach(([secondKey, items]) => {
          const secondLevelAggregated = aggregateData(
            items,
            availableColumns.measures
          );
          const secondGroupKey = `${secondField}_${secondKey}_${firstKey}`; // Include parent reference
          
          // Add second level group header
          result.push({
            [secondField]: secondKey,
            level: 1,
            isGroup: true,
            groupField: secondField,
            groupKey: secondGroupKey,
            parentGroupKey: `${firstField}_${firstKey}`, // Reference to parent group
            _originalData: items,
            _count: items.length,
            ...secondLevelAggregated,
          });

        // Sort items by rule_name before adding them
        const sortedItems = [...items].sort((a, b) => {
          const nameA = getRuleNameSortValue(a);
          const nameB = getRuleNameSortValue(b);
          return nameA.localeCompare(nameB);
        });

          // Add individual items as leaf nodes
          sortedItems.forEach((item) => {
            result.push({
              ...item,
              level: 2,
              isLeaf: true,
              _originalData: [item],
            });
          });
        });
      });

      return result;
    } else if (pivotConfig.groupBy.length === 3) {
      // Three level grouping
      const [firstField, secondField, thirdField] = pivotConfig.groupBy;
      const grouped = {};

      // Three level grouping
      filteredData.forEach((item) => {
        const key1 = item[firstField];
        if (!grouped[key1]) {
          grouped[key1] = {};
        }

        const key2 = item[secondField];
        if (!grouped[key1][key2]) {
          grouped[key1][key2] = {};
        }

        const key3 = item[thirdField];
        if (!grouped[key1][key2][key3]) {
          grouped[key1][key2][key3] = [];
        }
        grouped[key1][key2][key3].push(item);
      });

      const result = [];

      // Sort first level groups
      const sortedFirstLevelEntries = Object.entries(grouped).sort((a, b) => {
        const keyA = getGroupSortValue(firstField, a[0]);
        const keyB = getGroupSortValue(firstField, b[0]);
        return keyA.localeCompare(keyB);
      });

      sortedFirstLevelEntries.forEach(([firstKey, secondGroups]) => {
        // Calculate first level aggregation
        const firstLevelItems = Object.values(secondGroups).flat().flat();
        const firstLevelAggregated = aggregateData(
          firstLevelItems,
          availableColumns.measures
        );
        const firstGroupKey = `${firstField}_${firstKey}`;

        // Add first level group header
        result.push({
          [firstField]: firstKey,
          level: 0,
          isGroup: true,
          groupField: firstField,
          groupKey: firstGroupKey,
          _originalData: firstLevelItems,
          _count: Object.keys(secondGroups).length,
          ...firstLevelAggregated,
        });

        // Sort second level groups
        const sortedSecondLevelEntries = Object.entries(secondGroups).sort((a, b) => {
          const keyA = getGroupSortValue(secondField, a[0]);
          const keyB = getGroupSortValue(secondField, b[0]);
          return keyA.localeCompare(keyB);
        });

        // Process second level
        sortedSecondLevelEntries.forEach(([secondKey, thirdGroups]) => {
          const secondLevelItems = Object.values(thirdGroups).flat();
          const secondLevelAggregated = aggregateData(
            secondLevelItems,
            availableColumns.measures
          );
          const secondGroupKey = `${secondField}_${secondKey}_${firstKey}`;

          // Add second level group header
          result.push({
            [secondField]: secondKey,
            level: 1,
            isGroup: true,
            groupField: secondField,
            groupKey: secondGroupKey,
            parentGroupKey: firstGroupKey,
            _originalData: secondLevelItems,
            _count: Object.keys(thirdGroups).length,
            ...secondLevelAggregated,
          });

        // Sort third level groups
        const sortedThirdLevelEntries = Object.entries(thirdGroups).sort((a, b) => {
          const keyA = (a[0] || "").toString().toLowerCase();
          const keyB = (b[0] || "").toString().toLowerCase();
          return keyA.localeCompare(keyB);
        });

        // Process third level
        sortedThirdLevelEntries.forEach(([thirdKey, items]) => {
            const thirdLevelAggregated = aggregateData(
              items,
              availableColumns.measures
            );
            const thirdGroupKey = `${thirdField}_${thirdKey}_${secondKey}_${firstKey}`;

            // Add third level group header
            result.push({
              [thirdField]: thirdKey,
              level: 2,
              isGroup: true,
              groupField: thirdField,
              groupKey: thirdGroupKey,
              parentGroupKey: secondGroupKey,
              _originalData: items,
              _count: items.length,
              ...thirdLevelAggregated,
            });

          // Sort items by rule_name before adding them
          const sortedItems = [...items].sort((a, b) => {
            const nameA = getRuleNameSortValue(a);
            const nameB = getRuleNameSortValue(b);
            return nameA.localeCompare(nameB);
          });

            // Add individual items as leaf nodes
            sortedItems.forEach((item) => {
              result.push({
                ...item,
                level: 3,
                isLeaf: true,
                _originalData: [item],
              });
            });
          });
        });
      });

      return result;
    } else if (pivotConfig.groupBy.length === 4) {
      // Four level grouping
      const [firstField, secondField, thirdField, fourthField] =
        pivotConfig.groupBy;
      const grouped = {};

      // Four level grouping
      filteredData.forEach((item) => {
        const key1 = item[firstField];
        if (!grouped[key1]) {
          grouped[key1] = {};
        }

        const key2 = item[secondField];
        if (!grouped[key1][key2]) {
          grouped[key1][key2] = {};
        }

        const key3 = item[thirdField];
        if (!grouped[key1][key2][key3]) {
          grouped[key1][key2][key3] = {};
        }

        const key4 = item[fourthField];
        if (!grouped[key1][key2][key3][key4]) {
          grouped[key1][key2][key3][key4] = [];
        }
        grouped[key1][key2][key3][key4].push(item);
      });

      const result = [];

      // Sort first level groups
      const sortedFirstLevelEntries = Object.entries(grouped).sort((a, b) => {
        const keyA = getGroupSortValue(firstField, a[0]);
        const keyB = getGroupSortValue(firstField, b[0]);
        return keyA.localeCompare(keyB);
      });

      sortedFirstLevelEntries.forEach(([firstKey, secondGroups]) => {
        // Calculate first level aggregation
        const firstLevelItems = Object.values(secondGroups)
          .flat()
          .flat()
          .flat();
        const firstLevelAggregated = aggregateData(
          firstLevelItems,
          availableColumns.measures
        );
        const firstGroupKey = `${firstField}_${firstKey}`;

        // Add first level group header
        result.push({
          [firstField]: firstKey,
          level: 0,
          isGroup: true,
          groupField: firstField,
          groupKey: firstGroupKey,
          _originalData: firstLevelItems,
          _count: Object.keys(secondGroups).length,
          ...firstLevelAggregated,
        });

        // Sort second level groups
        const sortedSecondLevelEntries = Object.entries(secondGroups).sort((a, b) => {
          const keyA = getGroupSortValue(secondField, a[0]);
          const keyB = getGroupSortValue(secondField, b[0]);
          return keyA.localeCompare(keyB);
        });

        // Process second level
        sortedSecondLevelEntries.forEach(([secondKey, thirdGroups]) => {
          const secondLevelItems = Object.values(thirdGroups).flat().flat();
          const secondLevelAggregated = aggregateData(
            secondLevelItems,
            availableColumns.measures
          );
          const secondGroupKey = `${secondField}_${secondKey}_${firstKey}`;

          // Add second level group header
          result.push({
            [secondField]: secondKey,
            level: 1,
            isGroup: true,
            groupField: secondField,
            groupKey: secondGroupKey,
            parentGroupKey: firstGroupKey,
            _originalData: secondLevelItems,
            _count: Object.keys(thirdGroups).length,
            ...secondLevelAggregated,
          });

          // Sort third level groups
          const sortedThirdLevelEntries = Object.entries(thirdGroups).sort((a, b) => {
            const keyA = getGroupSortValue(thirdField, a[0]);
            const keyB = getGroupSortValue(thirdField, b[0]);
            return keyA.localeCompare(keyB);
          });

          // Process third level
          sortedThirdLevelEntries.forEach(([thirdKey, fourthGroups]) => {
            const thirdLevelItems = Object.values(fourthGroups).flat();
            const thirdLevelAggregated = aggregateData(
              thirdLevelItems,
              availableColumns.measures
            );
            const thirdGroupKey = `${thirdField}_${thirdKey}_${secondKey}_${firstKey}`;

            // Add third level group header
            result.push({
              [thirdField]: thirdKey,
              level: 2,
              isGroup: true,
              groupField: thirdField,
              groupKey: thirdGroupKey,
              parentGroupKey: secondGroupKey,
              _originalData: thirdLevelItems,
              _count: Object.keys(fourthGroups).length,
              ...thirdLevelAggregated,
            });

            // Sort fourth level groups
            const sortedFourthLevelEntries = Object.entries(fourthGroups).sort((a, b) => {
              const keyA = (a[0] || "").toString().toLowerCase();
              const keyB = (b[0] || "").toString().toLowerCase();
              return keyA.localeCompare(keyB);
            });

            // Process fourth level
            sortedFourthLevelEntries.forEach(([fourthKey, items]) => {
              const fourthLevelAggregated = aggregateData(
                items,
                availableColumns.measures
              );
              const fourthGroupKey = `${fourthField}_${fourthKey}_${thirdKey}_${secondKey}_${firstKey}`;

              // Add fourth level group header
              result.push({
                [fourthField]: fourthKey,
                level: 3,
                isGroup: true,
                groupField: fourthField,
                groupKey: fourthGroupKey,
                parentGroupKey: thirdGroupKey,
                _originalData: items,
                _count: items.length,
                ...fourthLevelAggregated,
              });

              // Sort items by rule_name before adding them
              const sortedItems = [...items].sort((a, b) => {
                const nameA = getRuleNameSortValue(a);
                const nameB = getRuleNameSortValue(b);
                return nameA.localeCompare(nameB);
              });

              // Add individual items as leaf nodes
              sortedItems.forEach((item) => {
                result.push({
                  ...item,
                  level: 4,
                  isLeaf: true,
                  _originalData: [item],
                });
              });
            });
          });
        });
      });

      return result;
    } else if (pivotConfig.groupBy.length === 5) {
      // Five level grouping
      const [firstField, secondField, thirdField, fourthField, fifthField] =
        pivotConfig.groupBy;
      const grouped = {};

      // Five level grouping
      filteredData.forEach((item) => {
        const key1 = item[firstField];
        if (!grouped[key1]) {
          grouped[key1] = {};
        }

        const key2 = item[secondField];
        if (!grouped[key1][key2]) {
          grouped[key1][key2] = {};
        }

        const key3 = item[thirdField];
        if (!grouped[key1][key2][key3]) {
          grouped[key1][key2][key3] = {};
        }

        const key4 = item[fourthField];
        if (!grouped[key1][key2][key3][key4]) {
          grouped[key1][key2][key3][key4] = {};
        }

        const key5 = item[fifthField];
        if (!grouped[key1][key2][key3][key4][key5]) {
          grouped[key1][key2][key3][key4][key5] = [];
        }
        grouped[key1][key2][key3][key4][key5].push(item);
      });

      const result = [];

      // Sort first level groups
      const sortedFirstLevelEntries = Object.entries(grouped).sort((a, b) => {
        const keyA = getGroupSortValue(firstField, a[0]);
        const keyB = getGroupSortValue(firstField, b[0]);
        return keyA.localeCompare(keyB);
      });

      sortedFirstLevelEntries.forEach(([firstKey, secondGroups]) => {
        // Calculate first level aggregation
        const firstLevelItems = Object.values(secondGroups)
          .flat()
          .flat()
          .flat()
          .flat();
        const firstLevelAggregated = aggregateData(
          firstLevelItems,
          availableColumns.measures
        );
        const firstGroupKey = `${firstField}_${firstKey}`;

        // Add first level group header
        result.push({
          [firstField]: firstKey,
          level: 0,
          isGroup: true,
          groupField: firstField,
          groupKey: firstGroupKey,
          _originalData: firstLevelItems,
          _count: Object.keys(secondGroups).length,
          ...firstLevelAggregated,
        });

        // Sort second level groups
        const sortedSecondLevelEntries = Object.entries(secondGroups).sort((a, b) => {
          const keyA = getGroupSortValue(secondField, a[0]);
          const keyB = getGroupSortValue(secondField, b[0]);
          return keyA.localeCompare(keyB);
        });

        // Process second level
        sortedSecondLevelEntries.forEach(([secondKey, thirdGroups]) => {
          const secondLevelItems = Object.values(thirdGroups)
            .flat()
            .flat()
            .flat();
          const secondLevelAggregated = aggregateData(
            secondLevelItems,
            availableColumns.measures
          );
          const secondGroupKey = `${secondField}_${secondKey}_${firstKey}`;

          // Add second level group header
          result.push({
            [secondField]: secondKey,
            level: 1,
            isGroup: true,
            groupField: secondField,
            groupKey: secondGroupKey,
            parentGroupKey: firstGroupKey,
            _originalData: secondLevelItems,
            _count: Object.keys(thirdGroups).length,
            ...secondLevelAggregated,
          });

          // Sort third level groups
          const sortedThirdLevelEntries = Object.entries(thirdGroups).sort((a, b) => {
            const keyA = getGroupSortValue(thirdField, a[0]);
            const keyB = getGroupSortValue(thirdField, b[0]);
            return keyA.localeCompare(keyB);
          });

          // Process third level
          sortedThirdLevelEntries.forEach(([thirdKey, fourthGroups]) => {
            const thirdLevelItems = Object.values(fourthGroups).flat().flat();
            const thirdLevelAggregated = aggregateData(
              thirdLevelItems,
              availableColumns.measures
            );
            const thirdGroupKey = `${thirdField}_${thirdKey}_${secondKey}_${firstKey}`;

            // Add third level group header
            result.push({
              [thirdField]: thirdKey,
              level: 2,
              isGroup: true,
              groupField: thirdField,
              groupKey: thirdGroupKey,
              parentGroupKey: secondGroupKey,
              _originalData: thirdLevelItems,
              _count: Object.keys(fourthGroups).length,
              ...thirdLevelAggregated,
            });

            // Sort fourth level groups
            const sortedFourthLevelEntries = Object.entries(fourthGroups).sort((a, b) => {
              const keyA = getGroupSortValue(fourthField, a[0]);
              const keyB = getGroupSortValue(fourthField, b[0]);
              return keyA.localeCompare(keyB);
            });

            // Process fourth level
            sortedFourthLevelEntries.forEach(([fourthKey, fifthGroups]) => {
              const fourthLevelItems = Object.values(fifthGroups).flat();
              const fourthLevelAggregated = aggregateData(
                fourthLevelItems,
                availableColumns.measures
              );
              const fourthGroupKey = `${fourthField}_${fourthKey}_${thirdKey}_${secondKey}_${firstKey}`;

              // Add fourth level group header
              result.push({
                [fourthField]: fourthKey,
                level: 3,
                isGroup: true,
                groupField: fourthField,
                groupKey: fourthGroupKey,
                parentGroupKey: thirdGroupKey,
                _originalData: fourthLevelItems,
                _count: Object.keys(fifthGroups).length,
                ...fourthLevelAggregated,
              });

              // Sort fifth level groups
              const sortedFifthLevelEntries = Object.entries(fifthGroups).sort((a, b) => {
                const keyA = getGroupSortValue(fifthField, a[0]);
                const keyB = getGroupSortValue(fifthField, b[0]);
                return keyA.localeCompare(keyB);
              });

              // Process fifth level
              sortedFifthLevelEntries.forEach(([fifthKey, items]) => {
                const fifthLevelAggregated = aggregateData(
                  items,
                  availableColumns.measures
                );
                const fifthGroupKey = `${fifthField}_${fifthKey}_${fourthKey}_${thirdKey}_${secondKey}_${firstKey}`;

                // Add fifth level group header
                result.push({
                  [fifthField]: fifthKey,
                  level: 4,
                  isGroup: true,
                  groupField: fifthField,
                  groupKey: fifthGroupKey,
                  parentGroupKey: fourthGroupKey,
                  _originalData: items,
                  _count: items.length,
                  ...fifthLevelAggregated,
                });

                // Sort items by rule_name before adding them
                const sortedItems = [...items].sort((a, b) => {
                  const nameA = getRuleNameSortValue(a);
                  const nameB = getRuleNameSortValue(b);
                  return nameA.localeCompare(nameB);
                });

                // Add individual items as leaf nodes
                sortedItems.forEach((item) => {
                  result.push({
                    ...item,
                    level: 5,
                    isLeaf: true,
                    _originalData: [item],
                  });
                });
              });
            });
          });
        });
      });

      return result;
    }

    return [];
  }, [
    rawData,
    pivotModeData,
    useFallbackData,
    pivotConfig.groupBy,
    pivotConfig.filters,
    availableColumns.measures,
  ]);

  // Flatten pivot data for display with proper hierarchical expansion
  const flattenedData = useMemo(() => {
    if (!pivotConfig.groupBy.length) return pivotData;
    
    const result = [];
    
    // For single level grouping
    if (pivotConfig.groupBy.length === 1) {
      pivotData.forEach((item) => {
        if (item.isGroup) {
          result.push(item);
          if (pivotConfig.expandedGroups.has(item.groupKey)) {
            // Add only the leaf items that belong to this specific group
            const groupField = pivotConfig.groupBy[0];
            const groupValue = item[groupField];
            const leafItems = pivotData.filter(
              (row) =>
                row.isLeaf && row.level === 1 && row[groupField] === groupValue
            );
            result.push(...leafItems);
          }
        }
      });
      return result;
    }
    
    // For two level grouping - clean hierarchical expansion
    if (pivotConfig.groupBy.length === 2) {
      // Process first level groups
      const firstLevelGroups = pivotData.filter(
        (row) => row.isGroup && row.level === 0
      );
      firstLevelGroups.forEach((firstGroup) => {
        result.push(firstGroup);
        
        // If first level is expanded, show its second level groups
        if (pivotConfig.expandedGroups.has(firstGroup.groupKey)) {
          // Find second level groups that belong to this first level group
          const secondLevelGroups = pivotData.filter((row) => {
            if (row.isGroup && row.level === 1) {
              // Use the parentGroupKey to establish proper relationships
              return row.parentGroupKey === firstGroup.groupKey;
            }
            return false;
          });
          
          // Process each second level group and its leaf items
          secondLevelGroups.forEach((secondGroup) => {
            result.push(secondGroup);
            
            // If second level group is expanded, show its leaf items immediately after
            if (pivotConfig.expandedGroups.has(secondGroup.groupKey)) {
              // Find leaf items that belong to this second level group
              const leafItems = pivotData.filter((row) => {
                if (row.isLeaf && row.level === 2) {
                  // Check if this leaf item belongs to both groups
                  const firstField = pivotConfig.groupBy[0];
                  const secondField = pivotConfig.groupBy[1];
                  return (
                    row[firstField] === firstGroup[firstField] &&
                    row[secondField] === secondGroup[secondField]
                  );
                }
                return false;
              });
              // Add leaf items immediately after their parent group
              result.push(...leafItems);
            }
          });
        }
      });
      return result;
    }

    // For three level grouping
    if (pivotConfig.groupBy.length === 3) {
      const [firstField, secondField, thirdField] = pivotConfig.groupBy;

      // Process first level groups
      const firstLevelGroups = pivotData.filter(
        (row) => row.isGroup && row.level === 0
      );
      firstLevelGroups.forEach((firstGroup) => {
        result.push(firstGroup);

        // If first level is expanded, show its second level groups
        if (pivotConfig.expandedGroups.has(firstGroup.groupKey)) {
          const secondLevelGroups = pivotData.filter((row) => {
            return (
              row.isGroup &&
              row.level === 1 &&
              row.parentGroupKey === firstGroup.groupKey
            );
          });

          secondLevelGroups.forEach((secondGroup) => {
            result.push(secondGroup);

            // If second level is expanded, show its third level groups
            if (pivotConfig.expandedGroups.has(secondGroup.groupKey)) {
              const thirdLevelGroups = pivotData.filter((row) => {
                return (
                  row.isGroup &&
                  row.level === 2 &&
                  row.parentGroupKey === secondGroup.groupKey
                );
              });

              thirdLevelGroups.forEach((thirdGroup) => {
                result.push(thirdGroup);

                // If third level is expanded, show its leaf items
                if (pivotConfig.expandedGroups.has(thirdGroup.groupKey)) {
                  const leafItems = pivotData.filter((row) => {
                    return (
                      row.isLeaf &&
                      row.level === 3 &&
                      row[firstField] === firstGroup[firstField] &&
                      row[secondField] === secondGroup[secondField] &&
                      row[thirdField] === thirdGroup[thirdField]
                    );
                  });
                  result.push(...leafItems);
                }
              });
            }
          });
        }
      });
      return result;
    }

    // For four level grouping
    if (pivotConfig.groupBy.length === 4) {
      const [firstField, secondField, thirdField, fourthField] =
        pivotConfig.groupBy;

      // Process first level groups
      const firstLevelGroups = pivotData.filter(
        (row) => row.isGroup && row.level === 0
      );
      firstLevelGroups.forEach((firstGroup) => {
        result.push(firstGroup);

        if (pivotConfig.expandedGroups.has(firstGroup.groupKey)) {
          const secondLevelGroups = pivotData.filter((row) => {
            return (
              row.isGroup &&
              row.level === 1 &&
              row.parentGroupKey === firstGroup.groupKey
            );
          });

          secondLevelGroups.forEach((secondGroup) => {
            result.push(secondGroup);

            if (pivotConfig.expandedGroups.has(secondGroup.groupKey)) {
              const thirdLevelGroups = pivotData.filter((row) => {
                return (
                  row.isGroup &&
                  row.level === 2 &&
                  row.parentGroupKey === secondGroup.groupKey
                );
              });

              thirdLevelGroups.forEach((thirdGroup) => {
                result.push(thirdGroup);

                if (pivotConfig.expandedGroups.has(thirdGroup.groupKey)) {
                  const fourthLevelGroups = pivotData.filter((row) => {
                    return (
                      row.isGroup &&
                      row.level === 3 &&
                      row.parentGroupKey === thirdGroup.groupKey
                    );
                  });

                  fourthLevelGroups.forEach((fourthGroup) => {
                    result.push(fourthGroup);

                    if (pivotConfig.expandedGroups.has(fourthGroup.groupKey)) {
                      const leafItems = pivotData.filter((row) => {
                        return (
                          row.isLeaf &&
                          row.level === 4 &&
                          row[firstField] === firstGroup[firstField] &&
                          row[secondField] === secondGroup[secondField] &&
                          row[thirdField] === thirdGroup[thirdField] &&
                          row[fourthField] === fourthGroup[fourthField]
                        );
                      });
                      result.push(...leafItems);
                    }
                  });
                }
              });
            }
          });
        }
      });
      return result;
    }

    // For five level grouping
    if (pivotConfig.groupBy.length === 5) {
      const [firstField, secondField, thirdField, fourthField, fifthField] =
        pivotConfig.groupBy;

      // Process first level groups
      const firstLevelGroups = pivotData.filter(
        (row) => row.isGroup && row.level === 0
      );
      firstLevelGroups.forEach((firstGroup) => {
        result.push(firstGroup);

        if (pivotConfig.expandedGroups.has(firstGroup.groupKey)) {
          const secondLevelGroups = pivotData.filter((row) => {
            return (
              row.isGroup &&
              row.level === 1 &&
              row.parentGroupKey === firstGroup.groupKey
            );
          });

          secondLevelGroups.forEach((secondGroup) => {
            result.push(secondGroup);

            if (pivotConfig.expandedGroups.has(secondGroup.groupKey)) {
              const thirdLevelGroups = pivotData.filter((row) => {
                return (
                  row.isGroup &&
                  row.level === 2 &&
                  row.parentGroupKey === secondGroup.groupKey
                );
              });

              thirdLevelGroups.forEach((thirdGroup) => {
                result.push(thirdGroup);

                if (pivotConfig.expandedGroups.has(thirdGroup.groupKey)) {
                  const fourthLevelGroups = pivotData.filter((row) => {
                    return (
                      row.isGroup &&
                      row.level === 3 &&
                      row.parentGroupKey === thirdGroup.groupKey
                    );
                  });

                  fourthLevelGroups.forEach((fourthGroup) => {
                    result.push(fourthGroup);

                    if (pivotConfig.expandedGroups.has(fourthGroup.groupKey)) {
                      const fifthLevelGroups = pivotData.filter((row) => {
                        return (
                          row.isGroup &&
                          row.level === 4 &&
                          row.parentGroupKey === fourthGroup.groupKey
                        );
                      });

                      fifthLevelGroups.forEach((fifthGroup) => {
                        result.push(fifthGroup);

                        if (
                          pivotConfig.expandedGroups.has(fifthGroup.groupKey)
                        ) {
                          const leafItems = pivotData.filter((row) => {
                            return (
                              row.isLeaf &&
                              row.level === 5 &&
                              row[firstField] === firstGroup[firstField] &&
                              row[secondField] === secondGroup[secondField] &&
                              row[thirdField] === thirdGroup[thirdField] &&
                              row[fourthField] === fourthGroup[fourthField] &&
                              row[fifthField] === fifthGroup[fifthField]
                            );
                          });
                          result.push(...leafItems);
                        }
                      });
                    }
                  });
                }
              });
            }
          });
        }
      });
      return result;
    }
    
    return result;
  }, [pivotData, pivotConfig.expandedGroups, pivotConfig.groupBy]);

  // Handle group expansion/collapse
  const toggleGroup = (groupKey, groupField) => {
    const newExpanded = new Set(pivotConfig.expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setPivotConfig((prev) => ({ ...prev, expandedGroups: newExpanded }));
  };

  // Handle grouping changes
  const toggleGroupBy = (field) => {
    const newGroupBy = pivotConfig.groupBy.includes(field)
      ? pivotConfig.groupBy.filter((f) => f !== field)
      : [...pivotConfig.groupBy, field];
    
    // Dynamic maximum levels based on available options
    const totalOptions = availableColumns.groupingOptions.length;
    const maxLevels = Math.min(
      Math.max(totalOptions, 1),
      MAX_GROUPING_LEVELS
    );

    // Limit grouping to maximum allowed levels
    if (newGroupBy.length > maxLevels) {
      newGroupBy.shift(); // Remove the oldest grouping field
    }

    // ✅ Build pivot_grouping object for backend
    const grouping = {};
    for (let i = 0; i < MAX_GROUPING_LEVELS; i++) {
      // grouping[`Hierachy_${i + 1}`] = newGroupBy[i] || "";
      grouping[`Hierarchy_${i + 1}`] = newGroupBy[i] || "";
    }

    // ✅ Send to parent (Summary → DataProduct)
    if (onGroupingChange) {
      onGroupingChange(grouping);
    }

    // Keep your old state update intact
    setPivotConfig((prev) => ({
      ...prev,
      groupBy: newGroupBy,
      expandedGroups: new Set(), // Reset expanded groups when grouping changes
    }));
  };
  

  // Reset grouping
  const resetGrouping = () => {
    setPivotConfig((prev) => ({
      ...prev,
      groupBy: [],
      expandedGroups: new Set(),
      filters: {},
    }));
  };

 
  // Sort data
  const sortData = (field) => {
    setPivotConfig((prev) => ({
      ...prev,
      sortBy: field,
      sortDirection:
        prev.sortBy === field && prev.sortDirection === "asc" ? "desc" : "asc",
    }));
  };

  // Get sorted data
  const sortedData = useMemo(() => {
    let dataToSort;

    if (pivotConfig.groupBy.length === 0) {
      // When not in pivot mode, use raw data with filters applied
      let filteredData = rawData;
      Object.entries(pivotConfig.filters).forEach(([field, value]) => {
        if (value) {
          filteredData = filteredData.filter((item) =>
            String(item[field])
              .toLowerCase()
              .includes(String(value).toLowerCase())
          );
        }
      });
      dataToSort = filteredData;
    } else {
      // When in pivot mode, use flattened pivot data
      dataToSort = flattenedData;
    }

    if (!pivotConfig.sortBy) return dataToSort;

    const field = pivotConfig.sortBy;

    return [...dataToSort].sort((a, b) => {
      const aVal = getSortableValue(a, field);
      const bVal = getSortableValue(b, field);

      const bothNumbers =
        typeof aVal === "number" && typeof bVal === "number" && !isNaN(aVal) && !isNaN(bVal);

      if (bothNumbers) {
        return pivotConfig.sortDirection === "asc"
          ? aVal - bVal
          : bVal - aVal;
      }

      const aStr = (aVal ?? "").toString();
      const bStr = (bVal ?? "").toString();
      const comparison = aStr.localeCompare(bStr, undefined, {
        sensitivity: "base",
      });

      return pivotConfig.sortDirection === "asc" ? comparison : -comparison;
    });
  }, [
    rawData,
    flattenedData,
    pivotConfig.groupBy.length,
    pivotConfig.filters,
    pivotConfig.sortBy,
    pivotConfig.sortDirection,
  ]);

  // Helper function to format rule name (remove prefix before '|')
  function formatRuleName(ruleName) {
    if (!ruleName || typeof ruleName !== "string") return "";

    const pipeIndex = ruleName.indexOf("|");
    if (pipeIndex !== -1) {
      return ruleName.substring(pipeIndex + 1).trim();
    }

    return ruleName.trim();
  }

  function getRuleNameSortValue(item) {
    if (!item) return "";
    const source =
      (typeof item.rule_name === "string" && item.rule_name) ||
      (typeof item.rule_description === "string" && item.rule_description) ||
      "";
    return formatRuleName(source).toLowerCase();
  }

  function getGroupDisplayValue(field, value) {
    if (field === "rule_name") {
      return formatRuleName(value);
    }
    return value ?? "";
  }

  function getGroupSortValue(field, value) {
    if (field === "rule_name") {
      return formatRuleName(value).toLowerCase();
    }
    return (value ?? "").toString().toLowerCase();
  }

  function getColumnLabel(field) {
    if (field === "rule_name") return "Rule Name";
    if (field === "rule_description") return "Rule Description";
    if (field === "error_record_count") return "ISSUES(COUNT)";

    return field
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  function getLeafContextValue(row, groupFields) {
    const contextFields = availableColumns.dimensions.filter(
      (field) => !groupFields.includes(field)
    );

    const parts = [];

    contextFields.forEach((field) => {
      const value = row[field];
      if (!value) {
        return;
      }

      if (field === "rule_name") {
        parts.push(formatRuleName(value));
      } else {
        parts.push(String(value));
      }
    });

    return parts.join(" | ");
  }

  function getSortableValue(item, field) {
    if (!item || !field) return "";

    if (field === "rule_name") {
      return getRuleNameSortValue(item);
    }

    if (field === "rule_description") {
      return (item.rule_description || "").toString().toLowerCase();
    }

    const value = item[field];

    if (typeof value === "number") {
      return value;
    }

    if (value === null || value === undefined) {
      return "";
    }

    return value.toString().toLowerCase();
  }

  // Helper function to format rule description with truncation and tooltip
  function formatRuleDescription(description) {
    if (!description || typeof description !== "string") return "";

    return description;
  }

  // Helper function to render rule description with tooltip for long text
  function renderRuleDescription(description) {
    if (!description || typeof description !== "string") return "";

    const formattedDescription = formatRuleDescription(description);

    return (
      <div
        className="rule-description-cell"
        title={formattedDescription}
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          wordBreak: "break-word",
          lineHeight: "1.5",
          maxHeight: "3em", // 2 lines * 1.5 line-height
        }}
      >
        {formattedDescription}
      </div>
    );
  }

  // Format quality metric values with conditional styling
  const formatQualityMetric = (value, field) => {
    if (!QUALITY_METRICS.includes(field)) {
      // For non-quality fields, use existing logic
      if (field === "error_record_count") {
        return Number(value || 0).toLocaleString();
      }
      return value !== null && value !== undefined
        ? `${Number(value || 0)}%`
        : "N/A";
    }

    // For quality fields, apply conditional styling
    if (value !== null && value !== undefined && value !== "") {
      return (
        <span style={{ fontWeight: "bold" }}>{`${Number(value || 0)}%`}</span>
      );
      } else {
      return <span style={{ fontWeight: "normal" }}>N/A</span>;
    }
  };

  // Skeleton loading component
  const SkeletonRow = ({ columns }) => (
    <tr className="skeleton-row">
      {columns.map((_, index) => (
        <td key={index} className="skeleton-cell">
          <div className="skeleton-shimmer"></div>
        </td>
      ))}
    </tr>
  );

const fetchExportData = async ({ showLoading = true } = {}) => { 
  if (showLoading) {
    setExportLoading(true);
    setExportError(null);
  }

  try {
    let url;
    
    if (dashboardId) {
      const period = activePeriod || "Current";
      url = `${BASE_URL}/dashboard-view/summary/${dashboardId}?page=1&page_size=10000&period=${period}`;
    } else {
      const query = new URLSearchParams();

      if (productId) query.append("product_id", productId);
      query.append("page", "1");
      query.append("page_size", "10000");
      
      const period = activePeriod || "Current";
      query.append("period", period);
      
      if (searchTerm) query.append("search", searchTerm);

      Object.entries(currentFilters).forEach(([key, value]) => {
        if (!value) return;
        const mappedKey = key.charAt(0).toUpperCase() + key.slice(1);
        if (Array.isArray(value)) {
          value.forEach(v => query.append("filter", `${mappedKey}=${v}`));
        } else {
          query.append("filter", `${mappedKey}=${value}`);
        }
      });

      url = `${BASE_URL}/product-overview?${query.toString()}`;
    }

    const response = await axios.get(url);

    if (!response.data || !response.data.tableData) {
      throw new Error("No data received from export API");
    }

    return response.data.tableData;
  } catch (err) {
    const errorMessage =
      err.response?.data?.message ||
      err.message ||
      "Error fetching export data";

    if (showLoading) {
      setExportError(errorMessage);
    }

    console.error("Error fetching export data:", err);
    throw err;
  } finally {
    if (showLoading) {
      setExportLoading(false);
    }
  }
};




  // Helper function to get file naming components
  const getFileNameComponents = () => {
    const getProductName = () => {
      try {
        const productName = localStorage.getItem("selectedProductName");
        if (productName && productName.trim()) {
          return productName
            .trim()
            .replace(/[^a-zA-Z0-9\s-_]/g, "")
            .replace(/\s+/g, "_");
        }
      } catch (error) {
        console.warn(
          "PivotTable: Error reading product name from localStorage:",
          error
        );
      }
      return "Product";
    };

    const getTimestamp = () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      return `${year}${month}${day}_${hours}${minutes}${seconds}`;
    };

    return { productName: getProductName(), timestamp: getTimestamp() };
  };

  // Excel export function with API integration
   const exportToExcel = async () => {
    try {
      // Validate required props - need either productId or dashboardId
      if (!productId && !dashboardId) {
        alert("Product ID or Dashboard ID is missing. Cannot export data.");
        return;
      }

      // Only validate currentFilters for regular product view (not for saved dashboards)
      if (!dashboardId && !currentFilters) {
        alert("Filters are missing. Cannot export data.");
        return;
      }

      // Fetch all export data from API
      let exportData;
      try {
        exportData = await fetchExportData();
      } catch (err) {
        alert(`Failed to fetch export data: ${exportError || "Unknown error"}`);
        return;
      }

      if (!exportData || exportData.length === 0) {
        alert("No data available to export");
        return;
      }

      // Dynamic import for xlsx library
      const XLSX = await import("xlsx");

      // Get hierarchy fields from filterList
      const hierarchyFields =
        filterList && Array.isArray(filterList)
          ? filterList.filter((f) => f && f.trim() !== "")
          : [];

      // Define measure columns
      const measureColumns = [
        "rule_name",
        "rule_description",
        "validity",
        "accuracy",
        "consistency",
        "completeness",
        "freshness",
        "uniqueness",
        "error_record_count",
      ];

      // Create headers
      const headers = [
        ...hierarchyFields.map(
          (field) => field.charAt(0).toUpperCase() + field.slice(1)
        ),
        ...measureColumns.map((field) =>
          field === "rule_name"
            ? "Rule Name"
            : field === "rule_description"
            ? "Rule Description"
            : field === "error_record_count"
            ? "ISSUES(COUNT)"
            : field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, " ")
        ),
      ];

      // Create worksheet data
      const worksheetData = exportData.map((row) => {
        return [
          ...hierarchyFields.map((field) => row[field] || "N/A"),
          ...measureColumns.map((field) => {
            if (field === "rule_name") {
              return formatRuleName(row[field]) || "N/A";
            } else if (field === "rule_description") {
              return formatRuleDescription(row[field]) || "N/A";
            } else if (field === "error_record_count") {
              return Number(row[field] || 0);
            } else {
              // For quality metrics (validity, accuracy, consistency, completeness, freshness, uniqueness)
              // keep the percentage format instead of converting to number
              return row[field] !== null && row[field] !== undefined && row[field] !== ""
                ? `${Number(row[field] || 0)}%`
                : "N/A";
            }
          }),
        ];
      });

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...worksheetData]);

      // Set column widths
      const columnWidths = headers.map((header) => {
        if (header === "Rule Name") {
          return { wch: 40 };
        } else if (header === "Rule Description") {
          return { wch: 50 }; // Wider for rule descriptions
        } else if (header === "ISSUES(COUNT)") {
          return { wch: 15 }; // Count column
    } else {
          return { wch: 12 }; // Standard width for other columns
        }
      });
      worksheet["!cols"] = columnWidths;

      // Style the header row (make headers bold)
      const headerRowNumber = 1;
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!worksheet[cellAddress]) continue;
        
        worksheet[cellAddress].s = {
          font: { bold: true },
          alignment: { horizontal: "center", vertical: "center" },
          fill: { fgColor: { rgb: "D3D3D3" } } // Light gray background
        };
      }

      // Add worksheet to workbook
      const { productName, timestamp } = getFileNameComponents();
      const sheetName = "Data Quality Report";
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // Generate Excel file with cell styles enabled
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Generate filename
      const filename = `${productName}_${timestamp}.xlsx`;

      // Download file
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
      link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert(`Error exporting data: ${error.message}. Please try again.`);
    }
  };



  return (
    <div className="pivot-table-container">
      {/* Control Panel - Only show when hierarchy data exists */}

      <div
        className={`${hasGroupingOptions ? "control-panel" : ""} ${
          !hasGroupingOptions ? "control-panel-nohierarchy" : ""
        }`}
      >
        <div
          className={`control-buttons ${
            !hasGroupingOptions ? "control-buttons-nohierarchy" : ""
          }`}
        >
          <div className="scroll-control-section">
           

            <ChevronLeft
  title="Scroll Left"
    className="cursor-pointer"
    onClick={() => handleTableScroll("left")}
  />
            <span className="scroll-label">Scroll</span>
          

             <ChevronRight
    className="cursor-pointer"
    title="Scroll Right"
    onClick={() => handleTableScroll("right")}
  />
          </div>

          <div className="control-actions">
            {hasGroupingOptions && (
              <div className="control-buttons-cont">
                <div className="group-by-section">
                  {availableColumns.groupingOptions.map((field) => {
                    const isSelected = pivotConfig.groupBy.includes(field);
                    const totalOptions = availableColumns.groupingOptions.length;
                    const selectedCount = pivotConfig.groupBy.length;
                    const maxLevels = Math.min(
                      Math.max(totalOptions, 1),
                      MAX_GROUPING_LEVELS
                    );

                    let shouldDisable = false;
                    let disableReason = "";

                    if (totalOptions === 1) {
                      shouldDisable = !isSelected;
                      disableReason = isSelected ? "" : "(Last option)";
                    } else {
                      shouldDisable = !isSelected && selectedCount >= maxLevels;
                      disableReason =
                        !isSelected && selectedCount >= maxLevels
                          ? `(Max ${maxLevels} level${
                              maxLevels > 1 ? "s" : ""
                            })`
                          : "";
                    }

                    return (
                      <label key={field} className="checkbox-label" style={readOnlyPivot ? { pointerEvents: "none" } : {}}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleGroupBy(field)}
                          disabled={shouldDisable}
                              style={readOnlyPivot ? { pointerEvents: "none" } : {}}

                        />
                        {formatGroupingLabel(field)}
                        {disableReason && (
                          <span style={{ color: "#999", fontSize: "0.8em" }}>
                            {" "}
                            {disableReason}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>

                <button onClick={resetGrouping} className="reset-btn" style={readOnlyPivot ? { pointerEvents: "none" } : {}}>
                  Reset Grouping
                </button>
              </div>
            )}

            {/* Export Button */}
            {tableData && tableData.length > 0 && (
              <div className="export-section">
                <button
                  onClick={exportToExcel}
                  className="export-btn-pivot"
                  disabled={exportLoading || (!productId && !dashboardId)}
                >
                  {exportLoading ? "Exporting..." : "Export"}
                </button>
              </div>
            )}
            {useFallbackData && pivotConfig.groupBy.length > 0 && exportError && <div className="control-messages">
              {exportError && (
                <div
                  style={{ color: "red", fontSize: "0.85em", marginTop: "5px" }}
                >
                  {exportError}
                </div>
              )}
              {useFallbackData && pivotConfig.groupBy.length > 0 && (
                <div
                  style={{
                    color: "orange",
                    fontSize: "0.85em",
                    marginTop: "5px",
                  }}
                >
                  ⚠️ Using paginated data due to API error. Some data may not be
                  visible.
                </div>
              )}
            </div>}
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="table-container" ref={tableContainerRef}>
        <table className="pivot-table">
          <thead>
            <tr>
              {isPivotMode ? (
                // When in pivot mode, show GROUP column plus non-grouped dimensions and measures
                <>
                  <th>GROUP</th>
                  {nonGroupedDimensions.map((field) => (
                    <th key={`pivot-context-${field}`} className="pivot-context-header">
                      {getColumnLabel(field)}
                    </th>
                  ))}
                  {availableColumns.measures.map((field) => {
                    const isSortable = field !== "error_record_count";
                    const label = getColumnLabel(field);
                    const suffix =
                      field === "error_record_count" ||
                      ["rule_name", "rule_description"].includes(field)
                        ? ""
                        : " (%)";

                    return (
                      <th
                        key={field}
                        onClick={isSortable ? () => sortData(field) : undefined}
                        className={`${isSortable ? "sortable-header" : ""} ${
                          field === "rule_name"
                            ? "rule-name-header"
                            : field === "rule_description"
                            ? "rule-description-header"
                            : QUALITY_METRICS.includes(field)
                            ? "quality-header"
                            : ""
                        }`}
                      >
                        {label}
                        {/* {suffix} */}
                        {isSortable && pivotConfig.sortBy === field && (
                          <span className="sort-indicator">
                            {pivotConfig.sortDirection === "asc" ? " ↑" : " ↓"}
                          </span>
                        )}
                      </th>
                    );
                  })}
                </>
              ) : hasHierarchyFields ? (
                // When not in pivot mode and hierarchy fields exist, show all columns
                <>
                  {availableColumns.dimensions.map((field) => (
                    <th key={field} className="normal-table-hierarchy-header">
                      {field.charAt(0).toUpperCase() + field.slice(1)}
                    </th>
                  ))}
                  {availableColumns.measures.map((field) => {
                    const isSortable = field !== "error_record_count";
                    const label =
                      field === "rule_name"
                        ? "Rule Name"
                        : field === "rule_description"
                        ? "Rule Description"
                        : field === "error_record_count"
                        ? "ISSUES(COUNT)"
                        : field.charAt(0).toUpperCase() +
                          field.slice(1).replace(/_/g, " ");
                    const suffix =
                      field === "error_record_count" ||
                      ["rule_name", "rule_description"].includes(field)
                        ? ""
                        : " (%)";
                    const isQualityMetric = QUALITY_METRICS.includes(field);
                    const headerClass =
                      field === "rule_name"
                        ? "normal-table-rule-name-header"
                        : field === "rule_description"
                        ? "normal-table-rule-description-header"
                        : isQualityMetric
                        ? "normal-table-quality-header"
                        : "normal-table-measure-header";

                    return (
                      <th
                        key={field}
                        onClick={isSortable ? () => sortData(field) : undefined}
                        className={`${isSortable ? "sortable-header" : ""} ${headerClass}`}
                      >
                        {label}
                        {/* {suffix} */}
                        {isSortable && pivotConfig.sortBy === field && (
                          <span className="sort-indicator">
                            {pivotConfig.sortDirection === "asc" ? " ↑" : " ↓"}
                          </span>
                        )}
                      </th>
                    );
                  })}
                </>
              ) : (
                // When no hierarchy fields exist, show only measure columns
                <>
                  {availableColumns.measures.map((field) => {
                    const isSortable = field !== "error_record_count";
                    const label =
                      field === "rule_name"
                        ? "Rule Name"
                        : field === "rule_description"
                        ? "Rule Description"
                        : field === "error_record_count"
                        ? "ISSUES(COUNT)"
                        : field.charAt(0).toUpperCase() +
                          field.slice(1).replace(/_/g, " ");
                    const suffix =
                      field === "error_record_count" ||
                      ["rule_name", "rule_description"].includes(field)
                        ? ""
                        : " (%)";

                    return (
                      <th
                        key={field}
                        onClick={isSortable ? () => sortData(field) : undefined}
                        className={`${isSortable ? "sortable-header" : ""} ${
                          field === "rule_name"
                            ? "rule-name-header"
                            : field === "rule_description"
                            ? "rule-description-header"
                            : QUALITY_METRICS.includes(field)
                            ? "quality-header"
                            : ""
                        }`}
                      >
                        {label}
                        {/* {suffix} */}
                        {isSortable && pivotConfig.sortBy === field && (
                          <span className="sort-indicator">
                            {pivotConfig.sortDirection === "asc" ? " ↑" : " ↓"}
                          </span>
                        )}
                      </th>
                    );
                  })}
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {(loading || pivotModeLoading) ? (
              // Show skeleton loading
              Array.from({ length: 5 }).map((_, index) => (
                <SkeletonRow
                  key={index}
                  columns={
                    isPivotMode
                      ? [
                          "GROUP",
                          ...nonGroupedDimensions,
                          ...availableColumns.measures,
                        ]
                      : hasHierarchyFields
                      ? [
                          ...availableColumns.dimensions,
                          ...availableColumns.measures,
                        ]
                      : availableColumns.measures
                  }
                />
              ))
            ) : error ? (
              // Show error message
              <tr>
                <td
                  colSpan={
                    isPivotMode
                      ? 1 + nonGroupedDimensions.length + availableColumns.measures.length
                      : hasHierarchyFields
                      ? availableColumns.dimensions.length +
                        availableColumns.measures.length
                      : availableColumns.measures.length
                  }
                  className="error-cell"
                >
                  <div className="error-message">{error}</div>
                    </td>
              </tr>
            ) : sortedData.length === 0 ? (
              // Show no data message
              <tr>
                <td
                  colSpan={
                    isPivotMode
                      ? 1 + nonGroupedDimensions.length + availableColumns.measures.length
                      : hasHierarchyFields
                      ? availableColumns.dimensions.length +
                        availableColumns.measures.length
                      : availableColumns.measures.length
                  }
                  className="no-data-cell"
                >
                  <div className="no-data-message">No Table Data Available</div>
                    </td>
              </tr>
            ) : isPivotMode ? (
              // When in pivot mode, show grouped structure
              flattenedData.map((row, index) => (
                <tr key={index} className={`table-row level-${row.level}`}>
                  <td className="group-cell">
                    <div style={{ paddingLeft: `${row.level * 20}px` }}>
                      {row.isGroup && (
                        <button
                          onClick={() =>
                            toggleGroup(row.groupKey, row.groupField)
                          }
                          className="expand-btn"
                        >
                          {pivotConfig.expandedGroups.has(row.groupKey)
                            ? "-"
                            : "+"}
                        </button>
                      )}
                      <span
                        className={row.isGroup ? "group-name" : "item-name"}
                      >
                        {row.isGroup
                          ? `${getGroupDisplayValue(
                              row.groupField,
                              row[row.groupField]
                            )} (${row._count || row._originalData.length})`
                          : getLeafContextValue(row, pivotConfig.groupBy)}
                      </span>
                    </div>
                  </td>
                  {nonGroupedDimensions.map((field) => {
                    const cellValue = row[field];
                    const displayValue = row.isGroup
                      ? ""
                      : cellValue && field === "rule_name"
                      ? formatRuleName(cellValue)
                      : cellValue || "";
                    return (
                      <td key={`${field}-${index}`} className="pivot-detail-cell">
                        {displayValue}
                      </td>
                    );
                  })}
                  {availableColumns.measures.map((field) => {
                    const isRuleName = field === "rule_name";
                    const isRuleDescription = field === "rule_description";
                    const isQualityMetric = QUALITY_METRICS.includes(field);
                    const cellClass = `measure-cell ${
                      isRuleName
                        ? "rule-name-cell"
                        : isRuleDescription
                        ? "rule-description-cell"
                        : isQualityMetric
                        ? "quality-metric-cell"
                        : ""
                    }`;

                    let content;
                    if (isRuleName) {
                      content = row.isGroup ? "" : formatRuleName(row[field]);
                    } else if (isRuleDescription) {
                      content = row.isGroup ? "" : renderRuleDescription(row[field]);
                    } else {
                      content = formatQualityMetric(row[field], field);
                    }

                    return (
                      <td key={field} className={cellClass}>
                        {content}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : hasHierarchyFields ? (
              // When hierarchy fields exist and not in pivot mode, show flat table with all columns
              sortedData.map((row, index) => (
                <tr key={index} className="table-row level-0">
                  {availableColumns.dimensions.map((field) => (
                    <td key={field} className="normal-table-hierarchy-cell">
                      {row[field] || ""}
                    </td>
                  ))}
                  {availableColumns.measures.map((field) => (
                    <td
                      key={field}
                      className={
                        field === "rule_name"
                          ? "normal-table-rule-name-cell"
                          : field === "rule_description"
                          ? "normal-table-rule-description-cell"
                          : QUALITY_METRICS.includes(field)
                          ? "normal-table-quality-cell"
                          : "normal-table-measure-cell"
                      }
                    >
                      {field === "rule_name"
                        ? formatRuleName(row[field])
                        : field === "rule_description"
                        ? renderRuleDescription(row[field])
                        : formatQualityMetric(row[field], field)}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              // When no hierarchy fields exist, show only measure columns
              sortedData
                .map((row, index) => {
                  try {
                    return (
                      <tr key={index} className="table-row level-0">
                        {availableColumns.measures.map((field) => {
                          try {
                            const isQualityMetric = QUALITY_METRICS.includes(field);
                            return (
                              <td
                                key={field}
                                className={`measure-cell ${
                                  field === "rule_name"
                                    ? "rule-name-cell"
                                    : field === "rule_description"
                                    ? "rule-description-cell"
                                    : isQualityMetric
                                    ? "quality-metric-cell"
                                    : ""
                                }`}
                              >
                                {field === "rule_name"
                                  ? formatRuleName(row[field])
                                  : field === "rule_description"
                                  ? renderRuleDescription(row[field])
                                  : formatQualityMetric(row[field], field)}
                              </td>
                            );
                          } catch (err) {
                            console.error(
                              "PivotTable: Error rendering cell:",
                              err
                            );
                            return (
                              <td key={field} className="measure-cell">
                                Error
                              </td>
                            );
                          }
                        })}
                      </tr>
                    );
                  } catch (err) {
                    console.error("PivotTable: Error rendering row:", err);
                    return null;
                  }
                })
                .filter(Boolean)
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination - Show when NOT in pivot mode OR when using fallback data in pivot mode */}
      {totalPages > 1 && (pivotConfig.groupBy.length === 0 ) && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>
              Showing {Math.max(1, (currentPage - 1) * pageSize + 1)} to{" "}
              {Math.min(currentPage * pageSize, Math.max(0, totalRecords))} of{" "}
              {Math.max(0, totalRecords)} entries
            </span>
          </div>

          <div className="pagination-controls">
         

            <div className="pagination-buttons">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="pagination-btn-pivot"
              >
                First
              </button>

              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn-pivot"
              >
                Previous
              </button>

              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`pagination-btn-pivot ${currentPage === pageNum ? "active" : ""
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn-pivot"
              >
                Next
              </button>

              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="pagination-btn-pivot"
              >
                Last
        </button>
      </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PivotTable;

